/**
 * Org Structure API
 *
 * Reads and edits the department tree that the hierarchy-based access layer
 * relies on (`OrgStructureRepository`, `access/record-visibility.ts` —
 * Tasks 1-9): who supervises whom, and how a department got its head —
 * manual assignment or CRM import.
 *
 * All three endpoints require `organization.manage_settings`, which the RBAC
 * matrix restricts to role `client` (org owner) plus the platform owner
 * (`packages/core/src/rbac/permissions.ts`) — `admin` gets a 403, not just a
 * narrower view. The UI mirrors that by hiding the tab entirely rather than
 * rendering it and letting every action fail (see settings/index.tsx).
 */
import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import {
  orgStructureResponseSchema,
  importOrgStructureResponseSchema,
} from '@aica/api-types/schemas';
import type {
  OrgStructureResponse,
  ImportOrgStructureResponse,
} from '@aica/api-types/schemas';

export class OrgStructureApi extends BaseApiClient {
  /**
   * Department tree, already sorted by materialized path (parent before its
   * children) — the caller derives nesting from `path`, it does not re-sort.
   */
  async getOrgStructure(orgId: string): Promise<OrgStructureResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.ORG_STRUCTURE(orgId),
      orgStructureResponseSchema
    );
  }

  /**
   * Import departments, members, and heads from the connected CRM.
   *
   * Step 1 (employee-email matching) and step 2 (fetching the department
   * tree) are separate server-side calls in sequence — a failure can happen
   * in either. If step 1 itself fails (403/500/503), nothing has been saved.
   * If step 1 succeeds and step 2 then fails, step 1's matching already
   * persisted. The response alone does not say which case occurred, so
   * callers must phrase the error neutrally — never claim nothing changed,
   * and never claim matching was definitely saved either. Both
   * `replaceFromImport` and the email-matching upsert are safe to repeat.
   */
  async importOrgStructure(orgId: string): Promise<ImportOrgStructureResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.ORG_STRUCTURE_IMPORT(orgId),
      importOrgStructureResponseSchema,
      { method: 'POST' }
    );
  }

  /**
   * Assign or clear (`userId: null`) a department's head. The server 404s if
   * the department isn't in this org or the user isn't a member — no Zod
   * response schema needed for a bare `{ success: true }`.
   */
  async setDepartmentHead(
    orgId: string,
    departmentId: string,
    userId: string | null
  ): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(
      API_ENDPOINTS.ORG_STRUCTURE_DEPARTMENT_HEAD(orgId, departmentId),
      {
        method: 'PATCH',
        body: JSON.stringify({ userId }),
      }
    );
  }
}
